import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyBranding } from "../services/api";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SignOutButton } from "./SignOutButton";

const BASE_MARK = "/branding/logo-mark.png";

/**
 * plan.md > Visual Design/Branding > Header logo layout: a fixed two-logo slot in the
 * upper-left corner. Slot 1 is always Base's own mark. Slot 2 shows the current company's logo
 * once uploaded (fetched via GET /company/:slug/branding), or Base's mark again as the default
 * — including throughout the whole CO Member portal, which is why `companySlug` is optional.
 */
export function AppHeader({ companySlug }: { companySlug?: string }) {
  const { t } = useTranslation();
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);

  useEffect(() => {
    if (!companySlug) {
      setCompanyLogoUrl(null);
      setCompanyName(null);
      return;
    }
    let cancelled = false;
    getCompanyBranding(companySlug)
      .then((branding) => {
        if (cancelled) return;
        setCompanyLogoUrl(branding.logoUrl);
        setCompanyName(branding.name);
      })
      .catch(() => {
        // Falls back to Base's own mark in slot 2 — matches the "no logo uploaded yet" case
        // (spec.md edge case), which is the right behavior for a fetch failure too.
        if (!cancelled) {
          setCompanyLogoUrl(null);
          setCompanyName(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug]);

  return (
    <header className="flex h-14 items-center gap-3 border-b border-surface-alt bg-surface px-4">
      <img src={BASE_MARK} alt="Base.vn" className="h-8 w-8" />
      {companyLogoUrl ? (
        <img src={companyLogoUrl} alt={companyName ?? "Company logo"} className="h-8 w-8 object-contain" />
      ) : (
        <img src={BASE_MARK} alt="Base.vn" className="h-8 w-8" />
      )}
      <span className="ml-2 font-sans text-ink">
        {companyName ?? t("common.appName", "Base.vn Onboarding Test Platform")}
      </span>
      <LanguageSwitcher />
      <SignOutButton />
    </header>
  );
}
